/**
 * THE CHART. The game's only interface, drawn as a torn paper chart lying over the sea.
 *
 * -- WHAT IT IS, AND WHAT IT REFUSES TO BE ------------------------------------
 *
 * Owner note, CONTRACT 1.5g note 4: "Currently seeing no GUI." There was none because round
 * 2 was told to ship none. This is it, and it is diegetic: painted on the 960x540 authoring
 * grid and doubled by the same integer 2 as the sea, so it is made of the game's own pixels
 * rather than laid over them in a browser's.
 *
 * -- THE READING OF SECTION 6 THAT THIS FILE GOT WRONG, AND THE OWNER CORRECTED TWICE ---
 *
 * Rounds 2 and 3 of surfaces read CONTRACT section 6's "PRESENCE CHANGES NOTHING" as a ban
 * on a permanently visible counter, and built accordingly: everything the player had earned
 * lived inside a ledger opened with a key nothing on screen named. The owner looked at the
 * running game and said "Currently seeing no GUI"; a round was spent adding to the ledger;
 * the owner looked again and said "there still is a lack of GUI". They were right both
 * times, and the clause never said what this file said it said. Quoting it whole: the game
 * "plays identically whether it is watched or not", and the instrument for it is that a
 * never-inputs controller and an always-inputs controller over one seed produce IDENTICAL
 * states. It is a claim about the simulation. It has nothing to say about whether the sheet
 * may write down what the save already holds, and a number a player cannot see is not a
 * number that cannot be earned while they are away - it is a number they are not told.
 *
 * The register was misread the same way. This file cited Sable and Journey, two of the four
 * games in the measured minimalist-indie set, and both are the diegetic pole: no persistent
 * chrome at all. The set's other two say the opposite. Hollow Knight - the widest capture in
 * that category, 15 usable frames against Sable's 2 - carries a SMALL PERMANENT CLUSTER in
 * one corner for the whole game: a row of masks, a soul vessel, and a drawn coin with a
 * numeral beside it. No panel, no plate, no label, no bar. That is the borrow, and it is a
 * borrow of STRUCTURE: a standing account, written in the surface's own hand, on the surface
 * that is already there.
 *
 * -- THE COMPOSITION -----------------------------------------------------------
 *
 * 64 art px of 540 - 11.9% - and the sea is never otherwise obscured. The strip did not grow
 * to carry the account; it had 18 blank rows under its second line and most of its width
 * empty, which is what "no GUI" looked like from the outside. Two poles and a foot:
 *
 *   LEFT    where the boat is and what it is doing, as a written phrase. Not a label and a
 *           value: "Over cold water, drifting north-east", in sentence case.
 *   RIGHT   the quarry as an ink drawing of the animal, the count landed beside it in the
 *           tally face, and under that the heaviest one ever landed. No species name - the
 *           drawing is the name, which is the whole reason it is a drawing.
 *   FOOT    the standing account, in the wash, the way a ledger page carries its own total
 *           at the bottom: what level, what is owed to the next, what is in the tin. While
 *           the sheet is lifted the same line carries the page's own key instead, because
 *           then you are reading the book rather than glancing at it.
 *
 * The foot is one line and it is a SENTENCE. Every fact on it is written the way a person
 * says it - "Level 41, 4,180 more to 42" - because a label-and-value pair is the instrument
 * panel this whole surface exists to refuse, and the difference between a HUD and a
 * dashboard is not how much it says but whether it says it in words.
 *
 * -- THE SIGNATURE, SPENT ONCE -------------------------------------------------
 *
 * The torn edge. It is the only ornament in the file, it is deterministic in x so it never
 * shimmers, and when the ledger lifts it RISES WITH THE PAPER rather than being drawn again
 * somewhere else - the signature moves, it is not respent.
 *
 * -- ONE ACCENT HUE, AND IT IS THE GAME'S OWN ----------------------------------
 *
 * RIM_RGB, the waterline rim the renderer already draws at every visible contact. It appears
 * nowhere on the paper. Its one job is under the catch mark on the water, where the game
 * already puts that colour. The measured corpus puts the median distinct accent count at 3
 * and the restrained registers at 0; the chart is at 0 and the water carries the one.
 */

import { MARKS } from './fishmark';
import {
  LINE_H,
  bitmap,
  drawHand,
  drawTally,
  handWidth,
  put,
  tallyWidth,
  type Bitmap,
} from './glyphs';
import {
  CHART_CAST_RGBS,
  CHART_DECKLE_RGB,
  CHART_GRAIN_RGB,
  CHART_INK_RGB,
  CHART_PAPER_RGB,
  CHART_SHADE_RGB,
  CHART_WASH_RGB,
  RIM_RGB,
  SHADOW_FACTORS,
  type Rgb,
} from './palette';
import type { RgbaImage } from './rotate';
import { ART_W } from './scale';

export const NONE = 0;
export const INK = 1;
export const WASH = 2;
export const PAPER = 3;
export const GRAIN = 4;
export const SHADE = 5;
export const DECKLE = 6;
export const RIM = 7;
/** The four steps of cast shadow on the water, deepest first, touching the paper. */
export const CAST = 8;

const COLOURS: readonly (Rgb | null)[] = [
  null,
  CHART_INK_RGB,
  CHART_WASH_RGB,
  CHART_PAPER_RGB,
  CHART_GRAIN_RGB,
  CHART_SHADE_RGB,
  CHART_DECKLE_RGB,
  RIM_RGB,
  ...CHART_CAST_RGBS,
];

/**
 * The rows of water above the tear that the sheet darkens. They are part of the chart's own
 * bitmap - the host offsets the sprite by exactly this - so the shadow can never drift away
 * from the edge that casts it.
 */
export const CAST_PAD = CHART_CAST_RGBS.length;

/** The closed strip: 64 of 540, which is 11.9%. */
export const STRIP_H = 64;
export const LEDGER_ROWS = 12;
const LEDGER_TOP = 12;
/**
 * THE LEAF'S HEADING, and why the sheet grew six pixels to carry it.
 *
 * Round 5's ledger was one page, so it needed no title: what else could a list of fish be?
 * It is two pages now - what to fish for, and where to sail - and a second page nobody can
 * see is a second page nobody has. The heading is how the leaf names itself and how the
 * other leaf announces that it exists, which is the whole reason it is written rather than
 * bound to a key: the anti-bar this project measured is a game whose players "could not
 * find anything" without a wiki open beside it.
 */
const HEAD_BASE = LEDGER_TOP + 8;
/** Baseline of the first ruled row, one line and a hair under the heading. */
const ROW0_BASE = HEAD_BASE + LINE_H + 2;
/** The lifted chart: 222 of 540, so 59% of the sea is still open behind it. */
export const OPEN_H = ROW0_BASE + (LEDGER_ROWS - 1) * LINE_H + 4 + STRIP_H;

/**
 * THE SHEET'S OWN LEFT AND RIGHT EDGES, inset from the frame.
 *
 * THIS IS A CHANGED PREMISE, NOT A CHANGED COORDINATE, and it is the ledger's own rule
 * firing: three passes at the colour and the tear all read as a SHORELINE, because a
 * full-bleed warm band with a ragged top across the bottom of a blue field IS a shoreline,
 * whatever value it is painted. A sheet with visible edges is unambiguously an object. The
 * top is torn, the bottom runs off frame, and the two sides are the chart's own cut edges
 * with the same measured contact shadow falling past them.
 */
const SHEET_INSET = 36;
const SHEET_X0 = SHEET_INSET;
const SHEET_X1 = ART_W - SHEET_INSET;

const MARGIN = SHEET_X0 + 22;
/**
 * Baselines measured DOWN from the panel's own bottom, so the writing stays put as it lifts.
 *
 * THREE LINES IN THE SAME 64 PIXELS TWO USED TO SIT IN. The strip's second line ended at row
 * 44 of 64 and the last 19 rows were bare paper; the foot goes there. The leading between the
 * second line and the foot is 14 rather than the ledger's 11, and that extra space is the ONLY
 * thing separating them - a hairline rule would be a fourth member of the mark family this
 * sheet already has three of (the mastery ink, the leaf heading, the locked wash), and the
 * open finding about that family says not to add to it.
 */
const PHRASE_BASE_UP = 39;
const ASIDE_BASE_UP = 26;
const FOOT_BASE_UP = 12;
const MARK_MID_UP = 33;

/** The ledger's two columns, and the gutter to their left that carries the two marks. */
const COL_W = 372;
const COL_X: readonly number[] = [MARGIN + 10, MARGIN + 10 + COL_W + 40];

/**
 * THE PLATE, AND THE 232 PIXELS A COLUMN NEVER NEEDED.
 *
 * -- THE OWNER'S QUESTION, AND THE ANSWER THAT IS NOT A DEFENCE --------------
 *
 * "Why aren't this fish using the graphics that match the quality of the bar". Because NO
 * FISH HAS EVER BEEN RENDERED. There was no fish batch under tools/spritegen/batches and no
 * fish under src/render/baked; the only baked art in this game is the boat, Murrin Roost,
 * the water sheet and the islets. Every fish a player has ever seen is a procedural
 * silhouette out of fishmark.ts. That is the whole answer and none of it is a technique
 * finding.
 *
 * -- WHAT THE SILHOUETTE'S OWN REASONING GOT RIGHT, AND WHERE IT STOPPED -----
 *
 * fishmark.ts says interior detail does not survive at the mark's size, and that is TRUE and
 * MEASURED. It is also a statement about a size nothing requires. Measured 2026-09-03:
 *
 *   the marks               37-63 art px on the long axis (the file's header says 28x14; it
 *                           is stale, the marks were re-authored at double size and the
 *                           header was not)
 *   the generation floor    about 100 art px, CONTRACT 1.2c - the BOAT falls out of band
 *                           below it on the same code path that made it
 *   the strip               64 art px tall, carrying three baselines. A fish at the floor is
 *                           about 55 px deep. It does not fit, and no arrangement makes it.
 *   a ledger row            11 art px. It does not fit.
 *   the catch mark          drawn on the water, which is metric space at 7.80 px/m, so a
 *                           100 px fish is a 13 m animal beside a 10 m boat. It does not fit
 *                           for a reason that has nothing to do with resolution.
 *
 * So the silhouette is CORRECT everywhere it is currently drawn, and the game had simply
 * never made the one place where a fish is looked at rather than indexed.
 *
 * -- WHERE THE ROOM WAS ------------------------------------------------------
 *
 * Here. A ruled row needs 72 art px for the longest species name and 32 for the widest
 * figure; it is given 372. Tightened to 150 the two columns end at 408 and everything from
 * 432 to 914 is free paper, 128 deep, WITHOUT growing the sheet and WITHOUT dropping a row -
 * all twenty-four species stay on the page at once, which is the thing that answers the
 * "I have no idea what is going on from the start" rejection.
 *
 *   the plate box           482 x 128 art px
 *   the boat, for scale     51 x 114
 *
 * Every one of the twenty-four clears the 100 px floor in that box; the deepest-bodied are
 * held by the 128 px height and still land near 130 on the long axis, and the longest run
 * past 400. The box is checked against the floor by chart.test.ts rather than asserted here.
 *
 * -- WHY IT IS INK AND WASH AND NOT A PAINTING -------------------------------
 *
 * This file paints into an INDEXED bitmap and the host composites two layers off it. A
 * full-colour fish needs a third layer, which is world-view.ts and not this lane's file - and
 * it would be the register break this project has already been rejected for, a painted object
 * lying on a two-colour paper sheet, a frame and its contents in two art languages. A field
 * guide does not print its plates in oil. The card paints the animal; gauntlet/art-technique/
 * fish-plates/plate.py reduces it to ink, wash and paper with the same 4x4 Bayer matrix this
 * file already dissolves the catch mark through, so the plate cannot bring a texture the
 * sheet does not have.
 *
 * -- IT IS OFF UNTIL ALL TWENTY-FOUR EXIST -----------------------------------
 *
 * `copy.plate` is optional and absent means the page is exactly what it was: two 372-wide
 * columns and no box. The design ledger's hardest rule is that two fidelity tiers of one
 * content class never ship on one surface, and four painted fish beside twenty stubs would
 * repeat the 2026-08-30 rejection precisely. The layout is here so that the round which
 * renders the batch has nothing left to design; it turns on when the twenty-fourth lands.
 */
/**
 * THE ROW'S THREE COLUMNS, measured rather than eyeballed.
 *
 * MEASURED with `handWidth` over the shipped copy, 2026-09-03:
 *
 *   the widest place name       62 art px ("Murrin Roost")
 *   the widest note             123 art px ("Shell Banks, smoked fish")
 *   the tally, four digits      under 40 art px
 *
 * So the note is given 130 and the figure 44, both measured from the column's RIGHT edge, and
 * the name keeps the whole of what is left. At the unplated 372 that puts the note's first
 * pixel at +198 and leaves 136 px of clear paper after the longest name - the row still reads
 * as three things far apart rather than as a table, which is the difference between a chart
 * and an instrument panel this whole surface exists to hold.
 *
 * A NARROWED COLUMN CANNOT COLLIDE WITH THIS: `plated` is species-only and a species row has
 * no note, so the two never appear together. The arithmetic is written against `colW` anyway,
 * because a fence that depends on a fact holding somewhere else is not a fence.
 */
const NOTE_W = 130;
const FIG_W = 44;

const PLATE_COL_W = 150;
const PLATE_COL_X: readonly number[] = [MARGIN + 10, MARGIN + 10 + PLATE_COL_W + 40];
const PLATE_X0 = MARGIN + 10 + PLATE_COL_W + 40 + PLATE_COL_W + 24;
const PLATE_X1 = SHEET_X1 - 10;
const PLATE_Y0 = HEAD_BASE + 6;
const PLATE_Y1 = HEAD_BASE + 6 + 128;

/** The plate box, in the sheet's own coordinates. Exported so a test can hold it to the floor. */
export const PLATE_BOX: Rect = { x0: PLATE_X0, y0: PLATE_Y0, x1: PLATE_X1, y1: PLATE_Y1 };

/** CONTRACT 1.2c: a generated object survives the grain fit down to about this, on its long axis. */
export const GENERATION_FLOOR_PX = 100;

/**
 * Whether this page is making room for a plate.
 *
 * THE LEAF IS PART OF THE CONDITION AND THAT IS NOT BELT AND BRACES. A plate is a plate OF A
 * SPECIES, and the two leaves do not have the same names on them: the longest species name is
 * 72 art px and fits a 150-wide column with room, but "The Weatherless Shoals" is 121 and the
 * tightened column clips its writing at 116. Keying the narrowing on the plate alone would
 * cut a landfall's name in half the first time a player pressed the second leaf, which is a
 * defect nothing in this round would have exercised because the plate is absent everywhere.
 */
function plated(copy: ChartCopy): boolean {
  return Boolean(copy.plate) && copy.leaf === 'species';
}

/** The ledger's column geometry, which narrows only when there is a plate to make room for. */
function cols(copy: ChartCopy): { readonly w: number; readonly x: readonly number[] } {
  return plated(copy) ? { w: PLATE_COL_W, x: PLATE_COL_X } : { w: COL_W, x: COL_X };
}
/** The hand, 11 px left of the writing; the target's blot, 6. Both inside the sheet. */
const HAND_DX = 11;
const BLOT_DX = 6;

/** Which page of the ledger is showing. */
export type Leaf = 'species' | 'places';

/** The heading each leaf writes at its own head. Plain, because finding things is the bar. */
export const LEAF_WORDS: Readonly<Record<Leaf, string>> = {
  species: 'What to fish for',
  places: 'Where to sail',
};

export const LEAVES: readonly Leaf[] = ['species', 'places'];

export interface LedgerRow {
  readonly name: string;
  /** The figure in the tally column: a landed count, or leagues off. Null when out of reach. */
  readonly count: number | null;
  /** The level it opens at, shown only when `count` is null. */
  readonly level: number;
  /** The row in force: the species being fished for, or the place the boat is lying at. */
  readonly marked: boolean;
  /** False for a row that cannot be chosen. A locked species is the only one. */
  readonly reachable: boolean;
  /**
   * THE ROW'S MIDDLE COLUMN: what this landfall is, in two or three words.
   *
   * "Wrack, kelp ash". The area first so the five areas form a column down the leaf, then the
   * one trade, which is what separates two islands lying the same distance off. Written in the
   * WASH, so the sheet's two written roles stay two - see `placeRows` for the whole argument.
   *
   * OPTIONAL, and absent is the shipping state for the species leaf and for any caller that
   * predates this: a row with no note is drawn exactly as it was, name and figure and nothing
   * between them.
   */
  readonly note?: string;
}

export interface ChartCopy {
  /** "Over cold water, drifting north-east". One line, sentence case, no label. */
  readonly phrase: string;
  /** The quiet second line, or the hold's disclosure while it holds. May be empty. */
  readonly aside: string;
  /** 0 gone, 1 fully written. Dithered, never blended - see `bayer`. */
  readonly asideInk: number;
  /** The species whose mark is drawn, or null when the save has landed nothing. */
  readonly markSpecies: string | null;
  readonly markCount: number;
  /**
   * THE HEAVIEST ONE EVER LANDED, written under its count. Empty when there is none.
   *
   * `SimState.records` is a per-species map and CONTRACT 1.5l(a) rules it earned, never
   * purchasable: "the ledger records a personal best". It is the one figure on this sheet a
   * player cannot make go up by playing longer in the ordinary way, so it is the one worth
   * standing where they can see it without asking.
   */
  readonly record: string;
  /**
   * THE FOOT OF THE PAGE. One line, one job at a time, always written.
   *
   * Shut, it is the standing account - level, what is owed to the next, coins - and, until
   * the player has opened the ledger once, the sentence that says the ledger is there. Open,
   * it is the page's own key, because the mastery measure under each row is otherwise a mark
   * with no explanation anywhere on the sheet.
   */
  readonly foot: string;
  /** 0 closed, 1 fully lifted. */
  readonly lift: number;
  readonly ledger: readonly LedgerRow[];
  /** Which page of the ledger the rows came from. */
  readonly leaf: Leaf;
  /** The row under the hand, or -1 when nothing is being pointed at. */
  readonly cursor: number;
  /**
   * THE WAY OUT, and it is the second verb's whole interface.
   *
   * The boat is off its own track - under orders, or made fast at the place those orders
   * delivered it to - so the strip carries the one thing that gives it back to the sea. It
   * exists only while there is something to give back, which is what keeps it off the sheet
   * the rest of the time: the chart has no permanent controls on it, and this is a control
   * precisely because it is temporary.
   *
   * IT IS WRITTEN, NOT DRAWN, AND THAT IS A CORRECTION MADE BY LOOKING. The first build put
   * a 9x5 pen mark here - a ruled track with a cross through it - and at the delivered 2x it
   * read as a speck of dust at the end of a sentence. A drawing is the right answer for the
   * quarry because a fish is a thing that has a picture; "give the boat back to its own
   * course" is not, and an abstract glyph for it fights a surface whose entire vocabulary is
   * somebody's handwriting. So the chart says it, in the hand it says everything else in,
   * and it is underlined the way the leaf you are on is underlined - which is this sheet's
   * one and only mark for "this is the live one".
   */
  readonly exit: boolean;
  /**
   * THE ANIMAL UNDER THE CURSOR, DRAWN. Absent until all twenty-four plates exist.
   *
   * Palette indices, the sheet's own: INK, WASH and PAPER, dithered. Absent is the shipping
   * state and it changes nothing - see PLATE_BOX above for why this is off rather than
   * partial, and for the measurement that says the box is there.
   */
  readonly plate?: Bitmap | null;
}

export interface Rect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * WHERE EVERY TOUCHABLE THING ON THE SHEET IS, IN THE SHEET'S OWN COORDINATES.
 *
 * y is measured DOWN FROM THE FIRST ROW OF PAPER - not from the bitmap's top, which carries
 * the cast shadow, and not from the frame's. The painter adds `CAST_PAD`; the host subtracts
 * the panel's top. Both do it in one line, and neither of them owns a copy of these numbers.
 *
 * ONE LAYOUT, TWO READERS. The first build of this had the painter place the rows and the
 * host guess at them from the same constants, which is two implementations of one fact and
 * drifts the first time either moves. A hit that lands on a row a player can see is the
 * entire correctness claim of a pointer interface, so it is computed once.
 */
export interface ChartLayout {
  readonly panelH: number;
  readonly headings: readonly { readonly leaf: Leaf; readonly rect: Rect }[];
  /**
   * Every row the lift has uncovered: where it is written, and WHICH row of `copy.ledger` it
   * is. The index is stored rather than implied by position because the two columns share a
   * set of baselines - half-way through a lift the uncovered rows are 0-7 and 12-19, and an
   * array whose fifth entry meant row 12 would hand a player the wrong fish. The painter
   * reads `x` and `base` from here too, so there is no second copy of the arithmetic.
   */
  readonly rows: readonly { readonly index: number; readonly x: number; readonly base: number; readonly rect: Rect }[];
  readonly exit: Rect | null;
}

/**
 * HOW MANY ROWS GO IN THE FIRST COLUMN, and why it is not always twelve.
 *
 * The species leaf is twenty-four rows and fills two columns of twelve exactly, which is
 * where the number came from. The places leaf is however many landfalls have been raised -
 * seventeen at two days of play - and twelve-then-five leaves the right half of the sheet
 * empty, which reads as a page that failed to finish rather than a page with less on it.
 * Splitting down the middle fills both columns evenly and leaves the species leaf untouched,
 * because half of twenty-four is twelve.
 */
function columnSplit(n: number): number {
  return Math.max(1, Math.min(LEDGER_ROWS, Math.ceil(n / 2)));
}

function headingRects(copy: ChartCopy): { readonly leaf: Leaf; readonly rect: Rect }[] {
  const out: { leaf: Leaf; rect: Rect }[] = [];
  // The headings start on the ROWS' own left edge, not the sheet's margin, so the blot that
  // says which leaf is in force sits in the same gutter as the blot that says which row is.
  let x = cols(copy).x[0] ?? MARGIN;
  for (const leaf of LEAVES) {
    const w = handWidth(LEAF_WORDS[leaf]);
    out.push({ leaf, rect: { x0: x - 4, y0: HEAD_BASE - 9, x1: x + w + 4, y1: HEAD_BASE + 4 } });
    x += w + 24;
  }
  return out;
}

/** What the way out is called, in the chart's own voice. Plain, and it says what happens. */
export const EXIT_WORDS = 'Let her drift.';

/** Where it sits: after the phrase it undoes, on the same baseline, with room to press it. */
function exitRect(copy: ChartCopy, panelH: number): Rect | null {
  if (!copy.exit) return null;
  const base = panelH - PHRASE_BASE_UP;
  const w = handWidth(EXIT_WORDS);
  const x = Math.min(MARGIN + handWidth(copy.phrase) + 22, SHEET_X1 - 22 - w);
  return { x0: x - 5, y0: base - 11, x1: x + w + 5, y1: base + 6 };
}

export function chartLayout(copy: ChartCopy): ChartLayout {
  const panelH = panelHeight(copy.lift);
  const rows: { index: number; x: number; base: number; rect: Rect }[] = [];
  // How much of the sheet the lift has actually uncovered. The rect stops one pixel short of
  // the rule under it, so two rows a pixel apart on the page are never a pixel of overlap
  // under the finger - which was the first build's off-by-two and would have made the top of
  // every row select the one above it.
  const uncovered = panelH - STRIP_H;
  const split = columnSplit(copy.ledger.length);
  const col = cols(copy);
  for (let i = 0; i < Math.min(copy.ledger.length, split * col.x.length); i++) {
    const base = ROW0_BASE + (i % split) * LINE_H;
    if (copy.lift <= 0 || base + 3 >= uncovered) continue;
    const x = col.x[Math.floor(i / split)] ?? MARGIN;
    rows.push({ index: i, x, base, rect: { x0: x - HAND_DX - 1, y0: base - 8, x1: x + col.w + 2, y1: base + 1 } });
  }
  return {
    panelH,
    headings: copy.lift > 0 && rows.length > 0 ? headingRects(copy) : [],
    rows,
    exit: exitRect(copy, panelH),
  };
}

export function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
}

export function panelHeight(lift: number): number {
  const t = Math.min(1, Math.max(0, lift));
  return Math.round(STRIP_H + t * (OPEN_H - STRIP_H));
}

/**
 * A 4x4 ordered dither, 0..15.
 *
 * THE ONLY WAY THIS FILE FADES ANYTHING. Alpha on a bitmap face at grain 2 would blend the
 * letter's edge into the paper and hand the player soft two-pixel type on the one surface
 * in the game that is read rather than looked at. A dissolve keeps every pixel hard and is
 * what the medium actually does.
 */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
function bayer(x: number, y: number): number {
  return BAYER[(y & 3) * 4 + (x & 3)] ?? 0;
}

/** A stable value in [0, 1) from a pair of integers. No clock, so the paper never crawls. */
function hash2(x: number, y: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * THE TORN EDGE. How far below the panel's top row the paper starts, at column x.
 *
 * THREE SCALES, and the coarsest one carries most of the amplitude. The first build put the
 * amplitude in an 11-pixel octave and the result read as a fine sand line rather than a tear:
 * paper parts in LONG runs with occasional deep bites, and it is the run length that says
 * torn. So: a 37-pixel octave worth 6 px, a 13-pixel octave worth 3, one pixel of jitter, and
 * a bite of 3 more on about one column in nine.
 *
 * Deterministic in x alone. The tear is a property of the sheet, so it must not change when
 * the boat moves, the clock runs or the chart lifts.
 */
function tearAt(x: number): number {
  const coarse = hash2(Math.floor(x / 37), 7);
  const mid = hash2(Math.floor(x / 13), 19);
  const jitter = hash2(x, 31);
  const bite = hash2(Math.floor(x / 5), 53) > 0.89 ? 3 : 0;
  return Math.round(coarse * 6 + mid * 3 + jitter) + bite;
}

/**
 * THE TWO PEN MARKS. Authored as pixels, like the face, for the same reason as the face.
 *
 * They are shapes and not colours, which is CONTRACT section 6's accessibility clause read
 * literally: the hand and the target are told apart by what they are drawn as, so the ledger
 * still reads with the hue thrown away.
 *
 *   HAND  a nib pointing at the row it is on. The keyboard's own position, always drawn when
 *         the ledger is open, because a cursor that only appears on hover is a cursor a
 *         keyboard cannot find.
 *   BLOT  ink laid on the entry in force. Solid, so it never reads as a letter.
 *
 * There were three. The way out was the third, and it is written now rather than drawn -
 * see ChartCopy.exit for why, which is the one design note in this file that came from
 * opening the capture rather than from reasoning about it.
 */
const HAND_ART = ['#..', '##.', '###', '##.', '#..'];
const BLOT_ART = ['###', '###', '###'];

function stamp(b: Bitmap, art: readonly string[], x: number, y: number, ink: number): void {
  for (let row = 0; row < art.length; row++) {
    const line = art[row] ?? '';
    for (let col = 0; col < line.length; col++) if (line[col] === '#') put(b, x + col, y + row, ink);
  }
}

function fill(b: Bitmap, x0: number, y0: number, w: number, h: number, ink: number): void {
  for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) put(b, x, y, ink);
}

/** Blit `src` into `dst`, skipping index 0, with an optional ordered-dither cut. */
function blit(dst: Bitmap, src: Bitmap, x0: number, y0: number, inkLevel = 16, remap?: (i: number) => number): void {
  for (let y = 0; y < src.h; y++) {
    for (let x = 0; x < src.w; x++) {
      const v = src.px[y * src.w + x] ?? 0;
      if (v === 0) continue;
      if (inkLevel < 16 && bayer(x0 + x, y0 + y) >= inkLevel) continue;
      put(dst, x0 + x, y0 + y, remap ? remap(v) : v);
    }
  }
}

/**
 * WHERE THE QUARRY POLE STANDS, so the painter and the writing budget read one answer.
 *
 * `left` is the first pixel the pole can occupy - the drawing's left edge when there is a
 * drawing, and the widest of the two figures otherwise. Everything written on this sheet is
 * capped 8 px short of it, which is why the sentences cannot run into the fish.
 */
function poleOf(copy: ChartCopy, right: number): {
  readonly right: number;
  readonly left: number;
  readonly figureX: number;
  readonly recordX: number;
} {
  if (copy.markSpecies === null) return { right, left: right, figureX: right, recordX: right };
  const figureW = tallyWidth(String(copy.markCount));
  const recordW = handWidth(copy.record);
  const blockW = Math.max(figureW, recordW);
  const mark = MARKS.get(copy.markSpecies);
  return {
    right,
    left: right - blockW - (mark ? 14 + mark.bmp.w : 0),
    figureX: right - figureW,
    recordX: right - recordW,
  };
}

/**
 * Paint the chart at the height its lift asks for. Pure: same copy in, same pixels out.
 */
export function paintChart(copy: ChartCopy): Bitmap {
  const h = panelHeight(copy.lift);
  const b = bitmap(ART_W, h + CAST_PAD);

  // The sheet, with its fibre. A flat fill is the failure this project has already been
  // rejected for once - "a flat solid rectangle... no texture" - so the paper has a material.
  for (let x = SHEET_X0; x < SHEET_X1; x++) {
    const top = CAST_PAD + tearAt(x);
    // The shadow it throws on the water above its torn edge.
    for (let k = 0; k < CAST_PAD; k++) put(b, x, top - 1 - k, CAST + k);
    // ONE noise tone, not two. The first build speckled a second, darker value through the
    // sheet as well; two textures doing one job is device density, which the design ledger
    // names as its own tell. This is the thing removed before finishing.
    for (let y = top; y < h + CAST_PAD; y++) {
      put(b, x, y, hash2(x, y) < 0.055 ? GRAIN : PAPER);
    }
    // The paper's own thickness where it parted: one or two pixels of exposed fibre, wider
    // where the tear bit deeper, which is what a torn edge actually does.
    put(b, x, top, DECKLE);
    if (hash2(x, 71) > 0.55) put(b, x, top + 1, DECKLE);
    // The sheet is cupped where it parted, so the first few rows under the tear sit in their
    // own shade. That gives the edge thickness rather than making it a colour change.
    for (let k = 0; k < 3; k++) put(b, x, top + 2 + k, SHADE);
  }

  // The two cut sides, and the shadow past them. The same nine-step measured ramp, laid
  // sideways: the corner it makes with the top band is what says "a sheet is lying here".
  const bottomRow = h + CAST_PAD;
  for (let d = 1; d <= CAST_PAD; d++) {
    const tint = CAST + d - 1;
    const leftFrom = CAST_PAD + tearAt(SHEET_X0) - (CAST_PAD - d);
    const rightFrom = CAST_PAD + tearAt(SHEET_X1 - 1) - (CAST_PAD - d);
    for (let y = Math.max(0, leftFrom); y < bottomRow; y++) put(b, SHEET_X0 - d, y, tint);
    for (let y = Math.max(0, rightFrom); y < bottomRow; y++) put(b, SHEET_X1 - 1 + d, y, tint);
  }
  // The cut edges themselves: one pixel of the sheet's own thickness down each side.
  for (let y = 0; y < bottomRow; y++) {
    if (y >= CAST_PAD + tearAt(SHEET_X0)) put(b, SHEET_X0, y, DECKLE);
    if (y >= CAST_PAD + tearAt(SHEET_X1 - 1)) put(b, SHEET_X1 - 1, y, DECKLE);
  }

  // Longer fibres, lying the way a fibre lies: along the sheet, never across it.
  for (let k = 0; k < 90; k++) {
    const fy = CAST_PAD + Math.floor(hash2(k, 3) * h);
    const fx = SHEET_X0 + 2 + Math.floor(hash2(k, 5) * (SHEET_X1 - SHEET_X0 - 14));
    const len = 3 + Math.floor(hash2(k, 9) * 9);
    for (let i = 0; i < len; i++) {
      if (fy > CAST_PAD + tearAt(fx + i) + 4) put(b, fx + i, fy, GRAIN);
    }
  }

  const bottom = h + CAST_PAD;
  const phraseBase = bottom - PHRASE_BASE_UP;
  const asideBase = bottom - ASIDE_BASE_UP;
  const footBase = bottom - FOOT_BASE_UP;
  const markMid = bottom - MARK_MID_UP;

  // THE QUARRY POLE IS MEASURED BEFORE ANYTHING IS WRITTEN, and the writing is stopped at it.
  //
  // The pole is now two figures deep - the count in the tally, the record under it in the hand
  // - and the drawing sits left of whichever is wider, so a long record can never be overrun
  // by the animal beside it. The three written lines are then capped at the drawing's own left
  // edge instead of at the sheet's, which is the fence chart-control.test.ts asserts by width
  // made true by construction: a sentence that would reach the pole is cut at a whole glyph.
  const pole = poleOf(copy, SHEET_X1 - 22);
  const writeTo = pole.left - 8;
  drawHand(b, MARGIN, phraseBase, copy.phrase, INK, writeTo);

  // The way out, after the phrase it undoes. Its rect is the layout's, so the words a player
  // aims at and the words the host tests against cannot be in two places.
  const way = exitRect(copy, h);
  if (way !== null) {
    const x = way.x0 + 5;
    drawHand(b, x, phraseBase, EXIT_WORDS, INK, writeTo);
    for (let rx = x; rx < x + handWidth(EXIT_WORDS); rx++) put(b, rx, phraseBase + 3, INK);
  }

  if (copy.aside.length > 0 && copy.asideInk > 0) {
    const level = Math.max(0, Math.min(16, Math.round(copy.asideInk * 16)));
    const line = bitmap(handWidth(copy.aside) + 2, LINE_H + 4);
    drawHand(line, 0, LINE_H - 1, copy.aside, WASH, writeTo - MARGIN);
    blit(b, line, MARGIN, asideBase - (LINE_H - 1), level);
  }

  // The foot. In the wash, because it is the page's own bookkeeping rather than anything the
  // sea just did, and the sheet's two written roles stay two.
  if (copy.foot.length > 0) drawHand(b, MARGIN, footBase, copy.foot, WASH, writeTo);

  // The quarry, at the other pole: the drawing, then the figure, then the record under it.
  // Right-aligned together so the strip reads as two things far apart rather than a row of
  // fields, and vertically on the same two baselines as the phrase and the line under it, so
  // the sheet reads as ruled rather than as two floating clusters.
  if (copy.markSpecies !== null) {
    const mark = MARKS.get(copy.markSpecies);
    drawTally(b, pole.figureX, phraseBase, String(copy.markCount), INK, pole.right);
    if (copy.record.length > 0) drawHand(b, pole.recordX, asideBase, copy.record, WASH, pole.right);
    if (mark) blit(b, mark.bmp, pole.left, markMid - mark.mid);
  }

  if (copy.lift > 0) {
    paintLedger(b, copy);
  }

  return b;
}

/**
 * THE LEDGER, which is the same sheet with more of it showing.
 *
 * Ruled rows, two columns, twelve each: every species this build ships, whether or not the
 * save has met it. That is deliberate and it answers a rejection this project has already
 * taken - "I have no idea what is going on from the start" - which was caused by applying
 * minimalism to INFORMATION rather than to chrome. A locked row still tells a player the
 * animal exists and what opens it.
 *
 * The lock is carried by the word "lv" and not by colour alone, which CONTRACT section 6
 * requires: every state distinguished by colour is also distinguished by shape or text.
 */
function paintLedger(b: Bitmap, copy: ChartCopy): void {
  const layout = chartLayout(copy);
  if (layout.rows.length === 0) return;

  // THE HEADINGS, AND THE TWO MARKS THIS PARAGRAPH USED TO SPEND AND NO LONGER DOES.
  //
  // An open finding says three marks on this sheet share one visual family: the mastery ink
  // laid along a row's rule, the underline under the leaf you are on, and the wash that made
  // the other leaf look like a locked row. All three are a tone or a horizontal run, and two
  // of them were spent here on a state that has a mark of its own already.
  //
  // So the heading in force now carries THE BLOT - the sheet's existing mark for "this is the
  // entry in force", a 3x3 solid that no letter and no rule can be confused with - in the same
  // gutter the rows put theirs in, and both headings are written in ink. That leaves exactly
  // one horizontal ink run on the sheet, the mastery measure, which is what makes labelling it
  // on the foot worth anything. It also satisfies CONTRACT section 6's accessibility clause
  // more strictly than the underline did: the leaf in force is now told by a SHAPE, and the
  // other heading is no longer distinguished from a locked row by tone alone, because it is
  // not distinguished by tone at all.
  for (const h of layout.headings) {
    const word = LEAF_WORDS[h.leaf];
    const x = h.rect.x0 + 4;
    const base = CAST_PAD + HEAD_BASE;
    drawHand(b, x, base, word, INK, SHEET_X1 - 22);
    if (h.leaf === copy.leaf) stamp(b, BLOT_ART, x - BLOT_DX, base - 4, INK);
  }

  const colW = cols(copy).w;
  // The plate goes down before the rows so that a rule can never be drawn over the animal,
  // and it is clipped to its own box rather than trusted to fit.
  if (plated(copy) && copy.plate) platePaint(b, copy.plate);

  for (const placed of layout.rows) {
    const row = copy.ledger[placed.index];
    if (!row) continue;
    const i = placed.index;
    const x = placed.x;
    const base = CAST_PAD + placed.base;
    const locked = !row.reachable;
    // THE MIDDLE COLUMN, when the row has one. Placed from the column's right edge so every
    // note on the leaf starts at the same pixel and the areas read as a column; the name is
    // then stopped short of it rather than at the figure, so a long name is cut by a whole
    // glyph instead of running into the note.
    const note = row.note ?? '';
    const noteX = x + colW - FIG_W - NOTE_W;
    const nameTo = note.length > 0 ? noteX - 8 : x + colW - 34;
    drawHand(b, x, base, row.name, locked ? WASH : INK, nameTo);
    if (note.length > 0) drawHand(b, noteX, base, note, WASH, x + colW - FIG_W - 4);
    if (row.count === null) {
      const lv = String(row.level);
      const w = tallyWidth(lv);
      drawTally(b, x + colW - w, base, lv, WASH, x + colW);
      drawHand(b, x + colW - w - handWidth('lv') - 4, base, 'lv', WASH, x + colW);
    } else {
      const fig = String(row.count);
      drawTally(b, x + colW - tallyWidth(fig), base, fig, locked ? WASH : INK, x + colW);
    }
    // The two gutter marks. The blot says which entry is in force; the hand says which one
    // the keyboard is on. Never the same shape, so neither has to be told apart by tone.
    if (row.marked) stamp(b, BLOT_ART, x - BLOT_DX, base - 4, INK);
    if (i === copy.cursor) stamp(b, HAND_ART, x - HAND_DX - 1, base - 5, INK);
    // The rule: under the row, in the paper's own shade, hairline. Structure, not decoration.
    // Under the entry in force it is ink, so the marked row is struck twice and by two shapes.
    for (let rx = x - 2; rx < x + colW + 2; rx++) put(b, rx, base + 3, row.marked ? INK : SHADE);
  }
}

/**
 * THE PLATE, CENTRED IN ITS BOX AND CLIPPED TO IT.
 *
 * Centred rather than left-set because the twenty-four are not one shape: an oarfish is a
 * ribbon four times the length of a sunfish's disc, and a page whose drawing jumps to a
 * different corner as the cursor moves reads as twenty-four layouts rather than one page.
 * Index 0 is the plate's own transparency and leaves the paper showing through, so the
 * animal sits ON the sheet rather than in a panel cut out of it - this file has no panels.
 */
function platePaint(b: Bitmap, plate: Bitmap): void {
  const ox = PLATE_X0 + Math.floor((PLATE_X1 - PLATE_X0 - plate.w) / 2);
  const oy = CAST_PAD + PLATE_Y0 + Math.floor((PLATE_Y1 - PLATE_Y0 - plate.h) / 2);
  for (let y = 0; y < plate.h; y++) {
    const py = PLATE_Y0 + Math.floor((PLATE_Y1 - PLATE_Y0 - plate.h) / 2) + y;
    if (py < PLATE_Y0 || py >= PLATE_Y1) continue;
    for (let x = 0; x < plate.w; x++) {
      const px = ox + x;
      if (px < PLATE_X0 || px >= PLATE_X1) continue;
      const v = plate.px[y * plate.w + x] ?? 0;
      if (v !== NONE) put(b, px, oy + y, v);
    }
  }
}

/**
 * THE CATCH MARK, on the water beside the boat. The only motion the interface has.
 *
 * It is not on the paper, so it does not use the paper's colours: ink for the animal, and
 * the game's own waterline rim one pixel under it, which is where that colour already lives.
 */
export function paintCatchMark(speciesId: string, ink: number): Bitmap | null {
  const mark = MARKS.get(speciesId);
  if (!mark) return null;
  const level = Math.max(0, Math.min(16, Math.round(ink * 16)));
  const w = mark.bmp.w + 2;
  const h = mark.bmp.h + 2;
  const out = bitmap(w, h);
  const at = (x: number, y: number): number =>
    x < 0 || y < 0 || x >= mark.bmp.w || y >= mark.bmp.h ? 0 : (mark.bmp.px[y * mark.bmp.w + x] ?? 0);

  for (let y = -1; y <= mark.bmp.h; y++) {
    for (let x = -1; x <= mark.bmp.w; x++) {
      if (bayer(x + 1, y + 1) >= level) continue;
      const v = at(x, y);
      if (v !== 0) {
        // The eye is paper on the chart; on the water it is the rim, so the mark never
        // borrows a colour the sea does not have.
        put(out, x + 1, y + 1, v === PAPER ? RIM : INK);
        continue;
      }
      // ONE PIXEL OF THE GAME'S OWN WATERLINE RIM, ALL ROUND. Drawn only below the silhouette
      // the mark read as a second solid object floating on the sea - the exact register break
      // this project has been rejected for before, a frame and its contents in two art
      // languages. A closed rim is what the renderer already draws at every visible contact,
      // so the mark becomes a thing IN the water rather than a shape on top of it.
      const touches =
        at(x - 1, y) !== 0 || at(x + 1, y) !== 0 || at(x, y - 1) !== 0 || at(x, y + 1) !== 0;
      if (touches) put(out, x + 1, y + 1, RIM);
    }
  }
  return out;
}

/** The bitmap as RGBA, with index 0 fully transparent: what a plate on a flat sea shows. */
export function toRgba(b: Bitmap): RgbaImage {
  const rgba = new Uint8ClampedArray(b.w * b.h * 4);
  for (let i = 0; i < b.px.length; i++) {
    const c = COLOURS[b.px[i] ?? 0];
    if (!c) continue;
    rgba[i * 4] = c[0];
    rgba[i * 4 + 1] = c[1];
    rgba[i * 4 + 2] = c[2];
    rgba[i * 4 + 3] = 255;
  }
  return { w: b.w, h: b.h, rgba };
}

/**
 * The bitmap as the TWO layers the renderer draws now the water moves: the sheet itself, and
 * the shadow it casts on the water as grey MULTIPLY factors (the same nine-step ramp, as
 * palette.ts SHADOW_FACTORS) so the cast darkens whatever the water is showing under the tear
 * rather than pasting a flat strip of the base over a passing crest. Same pixels, same ramp,
 * same offset; only the compositing changed. `toRgba` keeps the flat-sea reading for plates.
 */
export function toLayers(b: Bitmap): { paper: RgbaImage; cast: RgbaImage } {
  const paper = new Uint8ClampedArray(b.w * b.h * 4);
  const cast = new Uint8ClampedArray(b.w * b.h * 4);
  for (let i = 0; i < b.px.length; i++) {
    const idx = b.px[i] ?? 0;
    if (idx >= CAST) {
      const k = SHADOW_FACTORS[idx - CAST] ?? SHADOW_FACTORS[SHADOW_FACTORS.length - 1]!;
      cast[i * 4] = k;
      cast[i * 4 + 1] = k;
      cast[i * 4 + 2] = k;
      cast[i * 4 + 3] = 255;
      continue;
    }
    const c = COLOURS[idx];
    if (!c) continue;
    paper[i * 4] = c[0];
    paper[i * 4 + 1] = c[1];
    paper[i * 4 + 2] = c[2];
    paper[i * 4 + 3] = 255;
  }
  return { paper: { w: b.w, h: b.h, rgba: paper }, cast: { w: b.w, h: b.h, rgba: cast } };
}

export { fill };
